import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Grid,
  Autocomplete,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Delete,
  SwapHoriz,
  ArrowUpward,
  ArrowDownward,
  Save,
  PlayArrow,
  Pause,
  Stop,
  SkipPrevious,
  SkipNext,
  DragIndicator,
  Mic,
  Psychology,
  ExpandMore,
  ExpandLess,
  PictureAsPdf,
  CheckCircle,
  HourglassEmpty,
  QueueMusic,
} from '@mui/icons-material';
import PageHeader from '../components/PageHeader';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AiInsightsDialog from '../components/AiInsightsDialog';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;
const formatLocalDate = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const hexToRgba = (hex, alpha = 0.2) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Shared table styles for the pill-row layout
const headCellSx = {
  color: '#64748b',
  fontSize: '0.68rem',
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  borderBottom: 'none',
  pb: 1,
  pt: 0,
  whiteSpace: 'nowrap',
};

const tableSx = {
  borderCollapse: 'separate',
  borderSpacing: '0 5px',
  '& td': {
    border: 'none',
    py: 0.75,
  },
  '& tbody td:first-of-type': {
    borderTopLeftRadius: 10,
    borderBottomLeftRadius: 10,
  },
  '& tbody td:last-of-type': {
    borderTopRightRadius: 10,
    borderBottomRightRadius: 10,
  },
};

const timeCellSx = {
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontVariantNumeric: 'tabular-nums',
  fontSize: '0.8rem',
  color: 'var(--rs-muted)',
  whiteSpace: 'nowrap',
};

// Left colour accent that marks each row with its category colour
const accentCellSx = (color) => ({
  boxShadow: `inset 3px 0 0 0 ${color}`,
});

const emptyNewItem = () => ({
  asset_id: '',
  duration: 180,
  type: 'asset',
});

const collectItemCategoryIds = (item, asset) => {
  const ids = [];
  const push = (value) => {
    const id = Number(value);
    if (Number.isFinite(id) && id > 0) ids.push(id);
  };
  if (Array.isArray(item?.categories)) {
    item.categories.forEach((c) => push(c?.id));
  }
  if (item?.category && typeof item.category === 'object') {
    push(item.category.id);
  }
  push(item?.category_id || item?.categoryId);
  (asset?.categories || []).forEach((c) => push(c?.id));
  push(asset?.category_id || asset?.categoryId);
  return [...new Set(ids)];
};

const scoreReplacementCandidate = (candidate, { originalAsset, originalItem, categoryIds, occupiedAssetIds }) => {
  const candCats = (candidate.categories || []).map((c) => Number(c.id)).filter(Number.isFinite);
  const sharedCats = candCats.filter((id) => categoryIds.has(id)).length;
  if (categoryIds.size > 0 && sharedCats === 0) return null;

  const originalDuration = Number(originalAsset?.duration ?? originalItem?.duration) || 0;
  const candDuration = Number(candidate.duration) || 0;
  const durationDiff = Math.abs(candDuration - originalDuration);
  const durationRatio = originalDuration > 0 ? durationDiff / originalDuration : 1;

  let score = sharedCats * 100;
  if (originalDuration > 0 && candDuration > 0) {
    if (durationRatio <= 0.2) score += 40;
    else if (durationRatio <= 0.4) score += 20;
    else if (originalDuration <= 30 && durationDiff <= 8) score += 35;
    score -= Math.min(durationDiff / 5, 20);
  }
  const origPlayAs = originalAsset?.play_as || originalAsset?.playAs;
  const candPlayAs = candidate.play_as || candidate.playAs;
  if (origPlayAs && candPlayAs === origPlayAs) score += 15;
  const origSweeper = !!(originalAsset?.is_sweeper || originalAsset?.isSweeper || origPlayAs === 'sweeper');
  const candSweeper = !!(candidate.is_sweeper || candidate.isSweeper || candPlayAs === 'sweeper');
  if (origSweeper === candSweeper) score += 8;
  if (!occupiedAssetIds.has(Number(candidate.id))) score += 25;
  const origArtist = (originalAsset?.artist || originalItem?.artist || '').trim().toLowerCase();
  const candArtist = (candidate.artist || '').trim().toLowerCase();
  if (originalDuration > 90 && origArtist && candArtist && origArtist !== candArtist) score += 5;
  return score;
};

export default function PlaylistManager() {
  const { user, hasSystemFeature } = useAuth();
  const [stations, setStations] = useState([]);
  const [selectedStation, setSelectedStation] = useState('');
  const [selectedDate, setSelectedDate] = useState(formatLocalDate);
  const [selectedHour, setSelectedHour] = useState(new Date().getHours());
  const [playlistItems, setPlaylistItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [replacingIndex, setReplacingIndex] = useState(null);
  const [newItem, setNewItem] = useState(emptyNewItem);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('');
  const autoSuggestedRef = useRef(false);
  const [playingIndex, setPlayingIndex] = useState(null);
  const [playingVoicetrackId, setPlayingVoicetrackId] = useState(null);
  const [audioRef, setAudioRef] = useState(null);
  const [regions, setRegions] = useState([]);
  const [voicetracksByPlayoutLog, setVoicetracksByPlayoutLog] = useState({});
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [expandedBreaks, setExpandedBreaks] = useState(new Set());
  const [expandedMixblocks, setExpandedMixblocks] = useState(new Set());
  const [playingMixAssetId, setPlayingMixAssetId] = useState(null);
  const [mixPlayingTrackIdx, setMixPlayingTrackIdx] = useState(null);
  const [aiInsightsOpen, setAiInsightsOpen] = useState(false);
  const [aiInsightsItem, setAiInsightsItem] = useState(null);
  const playlistRequestRef = useRef(0);
  const playlistQueryRef = useRef('');
  const loadedPlaylistQueryRef = useRef('');
  const assetsRequestRef = useRef(0);
  const categoriesRequestRef = useRef(0);
  const regionsRequestRef = useRef(0);
  playlistQueryRef.current = `${selectedStation}|${selectedDate}|${selectedHour}`;
  const playlistMatchesSelection =
    !!selectedStation
    && loadedPlaylistQueryRef.current === playlistQueryRef.current
    && !loading;

  useEffect(() => {
    fetchStations();
    fetchCategories();
    if (user?.stationId) {
      setSelectedStation(user.stationId);
    }
  }, [user]);

  useEffect(() => {
    if (selectedStation && selectedDate !== null && selectedHour !== null) {
      // Drop editable state immediately so Save cannot write a stale hour/station.
      setPlaylistItems([]);
      loadedPlaylistQueryRef.current = '';
      setSelectedItems([]);
      setAddItemDialogOpen(false);
      setReplacingIndex(null);
      setNewItem(emptyNewItem());
      setSelectedCategoryFilter('');
      autoSuggestedRef.current = false;
      fetchPlaylist();
      if (selectedStation) {
        fetchAssets();
        fetchCategories();
        fetchRegions();
      }
    }
  }, [selectedStation, selectedDate, selectedHour]);

  // Clear asset cache when station changes so we don't show assets from the wrong station
  useEffect(() => {
    setAssets([]);
    setCategories([]);
    setRegions([]);
  }, [selectedStation]);

  // Load assets when dialog opens (cache cleared on station change so this always fetches fresh)
  useEffect(() => {
    if (addItemDialogOpen && selectedStation && assets.length === 0) {
      fetchAssets();
    }
  }, [addItemDialogOpen, selectedStation]);

  const fetchStations = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/stations`, {
        withCredentials: true
      });
      const list = res.data || [];
      setStations(list);
      if (list.length === 1) {
        setSelectedStation(list[0].id);
      } else if (!selectedStation && user?.stationId) {
        setSelectedStation(user.stationId);
      }
    } catch (error) {
      console.error('Error fetching stations:', error);
    }
  };

  const getTimelineDuration = (item, fallbackSeconds = 0) => {
    if (!item) return 0;

    // Timers/markers are positional anchors and should not stretch timeline math.
    if (item.type === 'hard_fixed_timer' || item.type === 'soft_time_marker') {
      return 0;
    }

    const duration = Number(item.duration);
    if (Number.isFinite(duration) && duration >= 0) return duration;
    return fallbackSeconds;
  };

  const recalculateTimes = (items, date = selectedDate, hour = selectedHour) => {
    if (items.length === 0) return items;
    
    const hourStart = new Date(`${date}T${hour.toString().padStart(2, '0')}:00:00`);
    const updated = [];
    let currentTime = new Date(hourStart);
    
    for (const item of items) {
      // Preserve all properties including type, title, artist, etc.
      const updatedItem = {
        ...item,
        startTime: new Date(currentTime).toISOString()
      };
      updated.push(updatedItem);
      // Keep marker/timer rows at zero so they don't push subsequent clock positions.
      const duration = getTimelineDuration(item, 180);
      currentTime = new Date(currentTime.getTime() + duration * 1000);
    }
    
    console.log('[recalculateTimes] Recalculated', updated.length, 'items');
    const vtMarkers = updated.filter(item => item.type === 'voicetrack');
    console.log('[recalculateTimes] VT markers found:', vtMarkers.length, vtMarkers.map(m => ({ title: m.title, artist: m.artist, type: m.type })));
    
    return updated;
  };

  const fetchPlaylist = async () => {
    if (!selectedStation || !selectedDate) return;
    const stationId = selectedStation;
    const date = selectedDate;
    const hour = selectedHour;
    const queryKey = `${stationId}|${date}|${hour}`;
    const requestId = ++playlistRequestRef.current;
    const isCurrent = () =>
      requestId === playlistRequestRef.current
      && queryKey === playlistQueryRef.current;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/playlist-generator/${stationId}/${date}/${hour}`,
        { withCredentials: true }
      );
      if (!isCurrent()) return;
      let items = res.data.items || [];
      
      // Items are already in the correct order from the M3U file (matching clock position order).
      // Recalculate times to ensure they're sequential from the hour start.
      items = recalculateTimes(items, date, hour);
      
      console.log('Fetched playlist items:', items);
      console.log('Total items:', items.length);
      if (items.length > 0) {
        console.log('First item structure:', {
          title: items[0].title,
          asset_id: items[0].asset_id,
          hasCategory: !!items[0].category,
          category: items[0].category,
          hasCategories: !!(items[0].categories && items[0].categories.length > 0),
          categories: items[0].categories,
          type: items[0].type,
          startTime: items[0].startTime
        });
        const itemsWithCategory = items.filter(item => item.category || (item.categories && item.categories.length > 0));
        console.log(`Items with category: ${itemsWithCategory.length} out of ${items.length}`);
      }
      loadedPlaylistQueryRef.current = queryKey;
      setPlaylistItems(items);
      fetchVoicetracksForPlaylist(items, { stationId, requestId, queryKey });
    } catch (error) {
      if (!isCurrent()) return;
      if (error.response?.status === 404) {
        // No playlist for this hour - start with empty
        loadedPlaylistQueryRef.current = queryKey;
        setPlaylistItems([]);
      } else {
        console.error('Error fetching playlist:', error);
        loadedPlaylistQueryRef.current = '';
        alert('Failed to load playlist: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      if (requestId === playlistRequestRef.current) setLoading(false);
    }
  };

  const fetchRegions = async () => {
    if (!selectedStation) return;
    const stationId = selectedStation;
    const requestId = ++regionsRequestRef.current;
    try {
      const res = await axios.get(`${API_URL}/api/regions?station_id=${stationId}`, { withCredentials: true });
      if (requestId !== regionsRequestRef.current || String(selectedStation) !== String(stationId)) return;
      setRegions((res.data || []).filter(r => r.is_active));
    } catch (error) {
      if (requestId !== regionsRequestRef.current || String(selectedStation) !== String(stationId)) return;
      console.error('Error fetching regions:', error);
      setRegions([]);
    }
  };

  const fetchVoicetracksForPlaylist = async (
    items,
    {
      stationId = selectedStation,
      requestId = playlistRequestRef.current,
      queryKey = playlistQueryRef.current,
    } = {}
  ) => {
    const isCurrent = () =>
      requestId === playlistRequestRef.current
      && queryKey === playlistQueryRef.current;
    if (!stationId || !items?.length) return;
    const markerIds = [...new Set(items.filter(i => (i.type === 'voicetrack' || i.type === 'voice') && i.id).map(i => i.id))];
    if (markerIds.length === 0) {
      if (isCurrent()) setVoicetracksByPlayoutLog({});
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/api/voicetrack-enhanced`, {
        params: { station_id: stationId, playout_log_ids: markerIds.join(',') },
        withCredentials: true
      });
      const list = res.data || [];
      const byPlayoutLog = {};
      list.forEach(vt => {
        const pid = vt.playout_log_id;
        if (!byPlayoutLog[pid]) byPlayoutLog[pid] = [];
        byPlayoutLog[pid].push(vt);
      });
      if (isCurrent()) setVoicetracksByPlayoutLog(byPlayoutLog);
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Error fetching voicetracks for playlist:', error);
      setVoicetracksByPlayoutLog({});
    }
  };

  const fetchAssets = async () => {
    if (!selectedStation) {
      console.warn('[PlaylistManager] No station selected, cannot fetch assets');
      setAssets([]);
      return;
    }

    const stationId = selectedStation;
    const requestId = ++assetsRequestRef.current;
    try {
      const res = await axios.get(`${API_URL}/api/assets`, {
        params: { station_id: stationId, playout_only: true },
        withCredentials: true
      });
      if (requestId !== assetsRequestRef.current || String(selectedStation) !== String(stationId)) return;
      const assetsData = res.data || [];
      console.log(`[PlaylistManager] Fetched ${assetsData.length} assets for station ${stationId}`);
      if (assetsData.length > 0) {
        const assetsWithCategories = assetsData.filter(a => a.categories && a.categories.length > 0);
        console.log(`[PlaylistManager] ${assetsWithCategories.length} assets have categories`);
        if (assetsWithCategories.length > 0) {
          console.log(`[PlaylistManager] Sample asset with category:`, {
            id: assetsWithCategories[0].id,
            title: assetsWithCategories[0].title,
            categories: assetsWithCategories[0].categories
          });
        }
      }
      setAssets(assetsData);
    } catch (error) {
      if (requestId !== assetsRequestRef.current || String(selectedStation) !== String(stationId)) return;
      console.error('Error fetching assets:', error);
      setAssets([]);
      alert('Failed to load assets. Please try again.');
    }
  };

  const fetchCategories = async () => {
    const stationId = selectedStation;
    const requestId = ++categoriesRequestRef.current;
    try {
      const res = await axios.get(`${API_URL}/api/categories`, {
        params: stationId ? { station_id: stationId } : {},
        withCredentials: true
      });
      if (
        requestId !== categoriesRequestRef.current
        || (stationId && String(selectedStation) !== String(stationId))
      ) return;
      setCategories(res.data || []);
    } catch (error) {
      if (
        requestId !== categoriesRequestRef.current
        || (stationId && String(selectedStation) !== String(stationId))
      ) return;
      console.error('Error fetching categories:', error);
    }
  };

  const handleSavePlaylist = async () => {
    if (!selectedStation || !selectedDate) return;
    const queryKey = `${selectedStation}|${selectedDate}|${selectedHour}`;
    if (loading || loadedPlaylistQueryRef.current !== queryKey) {
      alert('Playlist is still loading for this hour. Wait for it to finish before saving.');
      return;
    }
    const itemsToSave = playlistItems;
    const stationId = selectedStation;
    const date = selectedDate;
    const hour = selectedHour;

    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/playlist-generator/${stationId}/${date}/${hour}`,
        { items: itemsToSave },
        { withCredentials: true }
      );
      if (loadedPlaylistQueryRef.current === queryKey) {
        alert('Playlist saved successfully!');
      }
    } catch (error) {
      console.error('Error saving playlist:', error);
      alert('Failed to save playlist: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const closeItemDialog = () => {
    setAddItemDialogOpen(false);
    setNewItem(emptyNewItem());
    setSelectedCategoryFilter('');
    setReplacingIndex(null);
    autoSuggestedRef.current = false;
  };

  const openAddItemDialog = async () => {
    setReplacingIndex(null);
    setNewItem(emptyNewItem());
    setSelectedCategoryFilter('');
    autoSuggestedRef.current = false;
    if (selectedStation) {
      await fetchAssets();
    }
    setAddItemDialogOpen(true);
  };

  const handleAddItem = () => {
    if (!newItem.asset_id) {
      alert('Please select an asset');
      return;
    }
    
    const asset = assets.find(a => Number(a.id) === Number(newItem.asset_id));
    if (!asset) {
      alert('Asset not found');
      return;
    }

    applySelectedAsset(asset);
  };

  const applySelectedAsset = (asset) => {
    if (!asset) return;

    const isReplacing = replacingIndex !== null && replacingIndex >= 0 && replacingIndex < playlistItems.length;
    const originalItem = isReplacing ? playlistItems[replacingIndex] : null;

    const hourStart = new Date(`${selectedDate}T${selectedHour.toString().padStart(2, '0')}:00:00`);
    const lastItem = playlistItems[playlistItems.length - 1];
    let startTime = hourStart;
    
    if (lastItem && lastItem.startTime) {
      const lastTime = new Date(lastItem.startTime);
      const lastDuration = getTimelineDuration(lastItem, 180);
      startTime = new Date(lastTime.getTime() + lastDuration * 1000);
    }

    const replacementCategory =
      (selectedCategoryFilter
        ? asset.categories?.find((c) => Number(c.id) === Number(selectedCategoryFilter))
        : null)
      || asset.categories?.[0]
      || null;

    const assetDuration = Number(asset.duration);
    const fallbackDuration = Number(newItem.duration);
    const duration = Number.isFinite(assetDuration) && assetDuration > 0
      ? assetDuration
      : (Number.isFinite(fallbackDuration) && fallbackDuration > 0 ? fallbackDuration : 180);

    const newPlaylistItem = {
      title: asset.title || 'Unknown',
      artist: asset.artist || 'Unknown',
      duration,
      originalDuration: duration,
      filepath: asset.filepath,
      type: 'asset',
      startTime: originalItem?.startTime || startTime.toISOString(),
      asset_id: asset.id,
      category_id: replacementCategory?.id || originalItem?.category_id || null,
      category: replacementCategory,
      intro: asset.intro ?? null,
      cue_in: asset.cue_in ?? asset.cueIn ?? null,
      cue_out: asset.cue_out ?? asset.cueOut ?? null,
      start_next: asset.start_next ?? asset.startNext ?? null,
      fade_out_start: asset.fade_out_start ?? asset.fadeOutStart ?? null,
      play_as: asset.play_as || asset.playAs || 'normal',
      is_sweeper: !!(asset.is_sweeper || asset.isSweeper || asset.play_as === 'sweeper' || asset.playAs === 'sweeper'),
    };
    if (originalItem?.id != null) newPlaylistItem.id = originalItem.id;
    if (originalItem?.position != null) newPlaylistItem.position = originalItem.position;
    if (originalItem?.clockItemId != null) newPlaylistItem.clockItemId = originalItem.clockItemId;
    if (originalItem?.sourceHour != null) newPlaylistItem.sourceHour = originalItem.sourceHour;
    if (originalItem?.isDroppable != null) newPlaylistItem.isDroppable = originalItem.isDroppable;

    const updated = isReplacing
      ? playlistItems.map((item, index) => (index === replacingIndex ? newPlaylistItem : item))
      : [...playlistItems, newPlaylistItem];
    setPlaylistItems(recalculateTimes(updated));
    closeItemDialog();
  };

  const handleRemoveItem = (index) => {
    const updated = playlistItems.filter((_, i) => i !== index);
    setPlaylistItems(recalculateTimes(updated));
    // Remove from selection if it was selected
    setSelectedItems(prev => prev.filter(i => i !== index).map(i => i > index ? i - 1 : i));
  };

  // Handle item selection for VT marker insertion
  const handleItemSelect = (index) => {
    // Don't allow selecting voicetrack markers
    const item = playlistItems[index];
    if (item.type === 'voicetrack' || item.type === 'voice') {
      return;
    }

    setSelectedItems(prev => {
      // If item is already selected, deselect it
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      }
      // If we have 2 items selected, replace the first one
      if (prev.length >= 2) {
        return [prev[1], index];
      }
      // Otherwise add to selection
      return [...prev, index];
    });
  };

  // Add VT marker
  const addVTMarker = async () => {
    try {
      let insertIndex;
      let startTime;

      if (selectedItems.length === 2) {
        // Insert between the two selected items
        const [firstIndex, secondIndex] = [...selectedItems].sort((a, b) => a - b);
        insertIndex = firstIndex + 1;
        
        // Calculate start time based on the first selected item
        const firstItem = playlistItems[firstIndex];
        if (firstItem.startTime) {
          const firstItemTime = new Date(firstItem.startTime);
          const duration = getTimelineDuration(firstItem, 0);
          startTime = new Date(firstItemTime.getTime() + duration * 1000);
        } else {
          // Fallback: calculate from cumulative time
          let cumulativeTime = 0;
          for (let i = 0; i < firstIndex; i++) {
            cumulativeTime += getTimelineDuration(playlistItems[i], 0);
          }
          cumulativeTime += getTimelineDuration(firstItem, 0);
          const hourStart = new Date(`${selectedDate}T${selectedHour.toString().padStart(2, '0')}:00:00`);
          startTime = new Date(hourStart.getTime() + cumulativeTime * 1000);
        }
      } else {
        // Insert at the end
        insertIndex = playlistItems.length;
        if (playlistItems.length > 0) {
          const lastItem = playlistItems[playlistItems.length - 1];
          if (lastItem.startTime) {
            const lastItemTime = new Date(lastItem.startTime);
            const duration = getTimelineDuration(lastItem, 0);
            startTime = new Date(lastItemTime.getTime() + duration * 1000);
          } else {
            const hourStart = new Date(`${selectedDate}T${selectedHour.toString().padStart(2, '0')}:00:00`);
            const totalDuration = playlistItems.reduce((sum, item) => sum + getTimelineDuration(item, 0), 0);
            startTime = new Date(hourStart.getTime() + totalDuration * 1000);
          }
        } else {
          const hourStart = new Date(`${selectedDate}T${selectedHour.toString().padStart(2, '0')}:00:00`);
          startTime = hourStart;
        }
      }

      // Get logged-in user's name for artist field
      const artistName = user?.username || 
                        (user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : null) ||
                        user?.firstName ||
                        'VT';
      
      const newMarker = {
        type: 'voicetrack',
        title: 'VT Marker',
        artist: artistName,
        duration: 0,
        startTime: startTime.toISOString()
      };
      
      const updatedItems = [...playlistItems];
      updatedItems.splice(insertIndex, 0, newMarker);
      
      console.log('Adding VT marker at index', insertIndex, 'between items:', {
        before: playlistItems[insertIndex - 1]?.title,
        marker: newMarker,
        after: playlistItems[insertIndex]?.title
      });
      
      const recalculated = recalculateTimes(updatedItems);
      
      console.log('Recalculated items:', recalculated.length, 'items');
      console.log('VT marker in recalculated:', recalculated.find((item, idx) => idx === insertIndex));
      
      // Update local state first so user sees the change immediately
      console.log('[addVTMarker] Setting playlistItems state with', recalculated.length, 'items');
      console.log('[addVTMarker] VT markers in state:', recalculated.filter(item => item.type === 'voicetrack').map(m => ({ title: m.title, artist: m.artist, type: m.type })));
      setPlaylistItems(recalculated);
      
      // Clear selection after adding marker
      setSelectedItems([]);
      
      // Verify state was set correctly
      setTimeout(() => {
        console.log('[addVTMarker] State after setState - playlistItems length:', recalculated.length);
      }, 100);
      
      // Auto-save the playlist
      try {
        const saveResponse = await axios.put(
          `${API_URL}/api/playlist-generator/${selectedStation}/${selectedDate}/${selectedHour}`,
          { items: recalculated },
          { withCredentials: true }
        );
        
        console.log('VT marker saved successfully:', saveResponse.data);
        console.log('Current playlistItems state:', recalculated.length, 'items');
        
        // Verify the marker is still in state after save
        const currentState = recalculated;
        const markerInState = currentState.find(item => item.type === 'voicetrack' && item.title === 'VT Marker');
        console.log('Marker still in state after save:', !!markerInState);
        
        // Don't refresh immediately - keep the local state
        // The marker should now be in the saved playlist file
      } catch (error) {
        console.error('Error saving playlist:', error);
        alert('Failed to save VT marker: ' + (error.response?.data?.message || error.message));
        // Revert to previous state on error
        await fetchPlaylist();
      }
    } catch (error) {
      console.error('Error adding VT marker:', error);
      alert('Failed to add VT marker: ' + (error.response?.data?.message || error.message));
    }
  };

  // Drag and drop handlers
  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === index) return;

    const updated = [...playlistItems];
    const [moved] = updated.splice(draggedIndex, 1);
    updated.splice(index, 0, moved);
    
    setPlaylistItems(updated);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    if (draggedIndex !== null) {
      setPlaylistItems((current) => recalculateTimes(current));
    }
    setDraggedIndex(null);
  };

  const handleReplaceItem = async (index) => {
    const item = playlistItems[index];
    if (!item) return;
    autoSuggestedRef.current = false;
    setReplacingIndex(index);
    setNewItem({
      asset_id: '',
      duration: Number.isFinite(Number(item.duration)) ? Number(item.duration) : 180,
      type: item.type || 'asset',
    });
    const originalAsset = item.asset_id != null
      ? assets.find((a) => Number(a.id) === Number(item.asset_id))
      : null;
    const categoryIds = collectItemCategoryIds(item, originalAsset);
    setSelectedCategoryFilter(categoryIds[0] ? String(categoryIds[0]) : '');
    if (selectedStation && assets.length === 0) {
      await fetchAssets();
    }
    setAddItemDialogOpen(true);
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const date = new Date(timeString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Group consecutive commercial_break and mixblock_track items into collapsible blocks
  // with sequential display numbers and cumulative timing
  const groupCommercialBreaks = (items) => {
    const grouped = [];
    let i = 0;
    let displayNum = 1;
    let cumulativeTime = null;
    if (items.length > 0 && items[0].startTime) {
      cumulativeTime = new Date(items[0].startTime);
    } else if (selectedDate && selectedHour !== null) {
      cumulativeTime = new Date(`${selectedDate}T${String(selectedHour).padStart(2, '0')}:00:00`);
    }

    while (i < items.length) {
      if (items[i].type === 'commercial_break') {
        const breakGroup = [];
        const groupStartIndex = i;
        while (i < items.length && items[i].type === 'commercial_break') {
          breakGroup.push({ ...items[i], originalIndex: i });
          i++;
        }
        const totalDuration = breakGroup.reduce((sum, s) => sum + (s.duration || 0), 0);
        const groupTime = cumulativeTime ? new Date(cumulativeTime) : null;
        grouped.push({
          type: 'commercial_break_group',
          groupStartIndex,
          displayNum,
          items: breakGroup,
          spotCount: breakGroup.length,
          totalDuration,
          startTime: groupTime ? groupTime.toISOString() : breakGroup[0].startTime,
        });
        displayNum++;
        if (cumulativeTime) {
          cumulativeTime = new Date(cumulativeTime.getTime() + totalDuration * 1000);
        }
      } else if (items[i].type === 'mixblock_track') {
        const mbGroup = [];
        const groupStartIndex = i;
        const mbId = items[i].mixblock_id;
        const mbName = items[i].mixblock_name || 'Mixblock';
        
        mbGroup.push({ ...items[i], originalIndex: i });
        i++;

        while (i < items.length) {
          const hasMbId = items[i].mixblock_id != null;
          const isSameMb = hasMbId && items[i].mixblock_id === mbId;
          const isMbTrack = items[i].type === 'mixblock_track' && isSameMb;
          const isMbVt = (items[i].type === 'voicetrack' || items[i].type === 'ai_voicetrack') && isSameMb;
          
          if (!isMbTrack && !isMbVt) break;
          if (items[i].type === 'mixblock_track' && items[i].mixblock_position === 0) break;

          mbGroup.push({ ...items[i], originalIndex: i });
          i++;
        }
        const totalDuration = mbGroup.reduce((sum, s) => sum + (s.duration || 0), 0);
        const musicTracks = mbGroup.filter(t =>
          t.type === 'mixblock_track' && !t.is_sweeper && !t.isSweeper && t.play_as !== 'sweeper'
        );
        const groupTime = cumulativeTime ? new Date(cumulativeTime) : null;
        const firstTrack = mbGroup[0] || {};
        grouped.push({
          type: 'mixblock_group',
          groupStartIndex,
          displayNum,
          items: mbGroup,
          trackCount: musicTracks.length,
          totalDuration,
          mixblock_name: mbName,
          mixblock_id: mbId,
          startTime: groupTime ? groupTime.toISOString() : mbGroup[0].startTime,
          mix_status: firstTrack.mix_status || null,
          mix_asset_id: firstTrack.mix_asset_id || null,
          mix_filepath: firstTrack.mix_filepath || null,
          mix_queue_id: firstTrack.mix_queue_id || null,
          mix_track_offsets: firstTrack.mix_track_offsets || null,
        });
        displayNum++;
        if (cumulativeTime) {
          cumulativeTime = new Date(cumulativeTime.getTime() + totalDuration * 1000);
        }
      } else {
        const itemTime = cumulativeTime ? new Date(cumulativeTime) : null;
        const duration = items[i].duration || 0;
        grouped.push({
          ...items[i],
          originalIndex: i,
          displayNum,
          startTime: itemTime ? itemTime.toISOString() : items[i].startTime,
        });
        displayNum++;
        if (cumulativeTime) {
          cumulativeTime = new Date(cumulativeTime.getTime() + duration * 1000);
        }
        i++;
      }
    }
    return grouped;
  };

  const toggleBreakExpanded = (groupStartIndex) => {
    setExpandedBreaks(prev => {
      const next = new Set(prev);
      if (next.has(groupStartIndex)) {
        next.delete(groupStartIndex);
      } else {
        next.add(groupStartIndex);
      }
      return next;
    });
  };

  const toggleMixblockExpanded = (groupStartIndex) => {
    setExpandedMixblocks(prev => {
      const next = new Set(prev);
      if (next.has(groupStartIndex)) {
        next.delete(groupStartIndex);
      } else {
        next.add(groupStartIndex);
      }
      return next;
    });
  };

  const handlePlayItem = (index) => {
    const item = playlistItems[index];
    
    // Stop current playback if any
    if (audioRef) {
      audioRef.pause();
      audioRef.currentTime = 0;
    }

    // Construct audio URL
    let audioUrl;
    
    // For voicetrack markers, check for voicetrack_id first
    if (item.type === 'voicetrack' && item.voicetrack_id) {
      audioUrl = `${API_URL}/api/voicetrack-enhanced/${item.voicetrack_id}/audio`;
    } else if (item.type === 'voicetrack' && item.filepath) {
      // Fallback: try to use filepath if voicetrack_id not available
      let filename = item.filepath;
      if (filename.includes('/')) {
        filename = filename.split('/').pop();
      }
      // Voicetrack files might be in audio-cache/voicetracks/
      audioUrl = `${API_URL}/api/voicetrack-enhanced/audio/${filename}`;
    } else if (item.asset_id) {
      // First, try to use asset_id with streaming endpoint (best option)
      audioUrl = `${API_URL}/api/assets/${item.asset_id}/stream`;
    } else if (item.filepath) {
      // Extract filename from path (handle both absolute and relative paths)
      // Absolute paths like /home/rd/Documents/radiosuitex/files-xxx-yyy.mp3
      // Relative paths like files-xxx-yyy.mp3 or uploads/files-xxx-yyy.mp3
      let filename = item.filepath;
      
      // If it's an absolute path, extract just the filename
      if (filename.includes('/')) {
        filename = filename.split('/').pop();
      }
      
      // Remove any leading 'uploads/' prefix if present
      if (filename.startsWith('uploads/')) {
        filename = filename.replace('uploads/', '');
      }
      
      // Construct URL to uploads directory
      audioUrl = `${API_URL}/uploads/${filename}`;
    } else {
      alert('No audio file available for this item');
      return;
    }

    console.log('Playing audio from URL:', audioUrl);

    // Create new audio element
    const audio = new Audio(audioUrl);
    audio.addEventListener('ended', () => {
      setPlayingIndex(null);
      setAudioRef(null);
    });
    audio.addEventListener('error', (e) => {
      console.error('Audio playback error:', e);
      console.error('Failed URL:', audioUrl);
      console.error('Item data:', item);
      console.error('Audio element error details:', {
        error: audio.error,
        code: audio.error?.code,
        message: audio.error?.message
      });
      
      let errorMessage = 'Failed to play audio file.';
      if (audio.error) {
        switch (audio.error.code) {
          case audio.error.MEDIA_ERR_ABORTED:
            errorMessage = 'Audio playback was aborted.';
            break;
          case audio.error.MEDIA_ERR_NETWORK:
            errorMessage = 'Network error while loading audio.';
            break;
          case audio.error.MEDIA_ERR_DECODE:
            errorMessage = 'Audio decoding error. The file may be corrupted or in an unsupported format.';
            break;
          case audio.error.MEDIA_ERR_SRC_NOT_SUPPORTED:
            errorMessage = 'Audio format not supported or file not found.';
            break;
          default:
            errorMessage = `Failed to play audio: ${audio.error.message || 'Unknown error'}`;
        }
      }
      
      alert(errorMessage);
      setPlayingIndex(null);
      setAudioRef(null);
    });

    audio.play().catch((error) => {
      console.error('Error playing audio:', error);
      console.error('Failed URL:', audioUrl);
      console.error('Item data:', item);
      
      let errorMessage = 'Failed to play audio file: ' + error.message;
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Autoplay was blocked. Please click the play button again.';
      } else if (error.name === 'NotSupportedError') {
        errorMessage = 'Audio format not supported or file not found.';
      }
      
      alert(errorMessage);
      setPlayingIndex(null);
      setAudioRef(null);
    });
    
    setPlayingIndex(index);
    setAudioRef(audio);
  };

  const handleStopItem = () => {
    if (audioRef) {
      audioRef.pause();
      audioRef.currentTime = 0;
    }
    setPlayingIndex(null);
    setPlayingVoicetrackId(null);
    setPlayingMixAssetId(null);
    setMixPlayingTrackIdx(null);
    setAudioRef(null);
  };

  const handlePauseItem = () => {
    if (audioRef) {
      audioRef.pause();
    }
    setPlayingIndex(null);
    setPlayingVoicetrackId(null);
    setPlayingMixAssetId(null);
    setMixPlayingTrackIdx(null);
  };

  const handlePlayVoicetrackById = (vtId) => {
    if (audioRef) {
      audioRef.pause();
      audioRef.currentTime = 0;
    }
    const audioUrl = `${API_URL}/api/voicetrack-enhanced/${vtId}/audio`;
    const audio = new Audio(audioUrl);
    audio.addEventListener('ended', () => {
      setPlayingVoicetrackId(null);
      setAudioRef(null);
    });
    audio.addEventListener('error', () => {
      setPlayingVoicetrackId(null);
      setAudioRef(null);
    });
    audio.play().catch(() => {
      setPlayingVoicetrackId(null);
      setAudioRef(null);
    });
    setPlayingVoicetrackId(vtId);
    setPlayingIndex(null);
    setAudioRef(audio);
  };

  const handlePlayMixedFile = (mixAssetId, trackOffsets, startTrackIdx = 0) => {
    if (audioRef) {
      audioRef.pause();
      audioRef.currentTime = 0;
    }
    const audioUrl = `${API_URL}/api/assets/${mixAssetId}/stream`;
    const audio = new Audio(audioUrl);
    const offsets = trackOffsets || [];

    audio.addEventListener('ended', () => {
      setPlayingMixAssetId(null);
      setMixPlayingTrackIdx(null);
      setPlayingIndex(null);
      setAudioRef(null);
    });
    audio.addEventListener('error', () => {
      setPlayingMixAssetId(null);
      setMixPlayingTrackIdx(null);
      setPlayingIndex(null);
      setAudioRef(null);
    });
    audio.addEventListener('timeupdate', () => {
      if (offsets.length === 0) return;
      const t = audio.currentTime;
      let idx = 0;
      for (let i = offsets.length - 1; i >= 0; i--) {
        if (t >= (offsets[i].offset || 0)) { idx = i; break; }
      }
      setMixPlayingTrackIdx(idx);
    });

    audio.play().then(() => {
      if (startTrackIdx > 0 && offsets[startTrackIdx]) {
        audio.currentTime = offsets[startTrackIdx].offset || 0;
      }
    }).catch((err) => {
      alert('Failed to play mixed file: ' + err.message);
      setPlayingMixAssetId(null);
      setMixPlayingTrackIdx(null);
      setAudioRef(null);
    });

    setPlayingMixAssetId(mixAssetId);
    setMixPlayingTrackIdx(startTrackIdx);
    setPlayingIndex(null);
    setPlayingVoicetrackId(null);
    setAudioRef(audio);
  };

  const handleSkipMixTrack = (mixAssetId, trackOffsets, direction) => {
    if (!audioRef || playingMixAssetId !== mixAssetId) return;
    const offsets = trackOffsets || [];
    const currentIdx = mixPlayingTrackIdx || 0;
    const nextIdx = direction === 'next'
      ? Math.min(currentIdx + 1, offsets.length - 1)
      : Math.max(currentIdx - 1, 0);
    if (nextIdx !== currentIdx && offsets[nextIdx]) {
      audioRef.currentTime = offsets[nextIdx].offset || 0;
      setMixPlayingTrackIdx(nextIdx);
    }
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef) {
        audioRef.pause();
        audioRef.currentTime = 0;
      }
    };
  }, [audioRef]);

  const totalDuration = playlistItems.reduce((sum, item) => sum + (item.duration || 0), 0);
  const groupedPlaylistEntries = useMemo(
    () => groupCommercialBreaks(playlistItems),
    [playlistItems, selectedDate, selectedHour]
  );
  const assetById = useMemo(() => {
    const map = new Map();
    assets.forEach((asset) => {
      const assetId = typeof asset.id === 'string' ? parseInt(asset.id, 10) : asset.id;
      if (!Number.isNaN(assetId)) {
        map.set(assetId, asset);
      }
    });
    return map;
  }, [assets]);
  const regionNameById = useMemo(() => {
    const map = new Map();
    regions.forEach((region) => {
      map.set(region.id, region.name);
    });
    return map;
  }, [regions]);
  const selectedItemSet = useMemo(() => new Set(selectedItems), [selectedItems]);
  const replacingItem = replacingIndex !== null ? playlistItems[replacingIndex] : null;
  const replacementSuggestions = useMemo(() => {
    if (!replacingItem) return [];
    const originalAsset = replacingItem.asset_id != null
      ? assetById.get(Number(replacingItem.asset_id))
      : null;
    const categoryIds = selectedCategoryFilter
      ? new Set([Number(selectedCategoryFilter)].filter((id) => Number.isFinite(id) && id > 0))
      : new Set(collectItemCategoryIds(replacingItem, originalAsset));
    const originalId = Number(replacingItem.asset_id);
    const occupiedAssetIds = new Set(
      playlistItems
        .filter((_, index) => index !== replacingIndex)
        .map((item) => Number(item.asset_id))
        .filter((id) => Number.isFinite(id) && id > 0)
    );

    return assets
      .filter((asset) => Number(asset.id) !== originalId)
      .map((asset) => {
        const score = scoreReplacementCandidate(asset, {
          originalAsset,
          originalItem: replacingItem,
          categoryIds,
          occupiedAssetIds,
        });
        return score == null ? null : { asset, score };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score || (a.asset.title || '').localeCompare(b.asset.title || ''))
      .slice(0, 8)
      .map((entry) => entry.asset);
  }, [assets, assetById, playlistItems, replacingIndex, replacingItem, selectedCategoryFilter]);

  useEffect(() => {
    if (!addItemDialogOpen || !replacingItem || autoSuggestedRef.current) return;
    const originalAsset = replacingItem.asset_id != null
      ? assetById.get(Number(replacingItem.asset_id))
      : null;
    const categoryIds = collectItemCategoryIds(replacingItem, originalAsset);
    if (!selectedCategoryFilter && categoryIds[0]) {
      setSelectedCategoryFilter(String(categoryIds[0]));
      return;
    }
    if (replacementSuggestions[0]) {
      autoSuggestedRef.current = true;
      const suggestion = replacementSuggestions[0];
      setNewItem((prev) => ({
        ...prev,
        asset_id: suggestion.id.toString(),
        duration: suggestion.duration ?? prev.duration,
      }));
    }
  }, [addItemDialogOpen, replacingItem, assetById, selectedCategoryFilter, replacementSuggestions]);

  const downloadHourPdf = () => {
    if (!playlistItems || playlistItems.length === 0) return;

    const doc = new jsPDF({ orientation: 'landscape' });
    const stationObj = stations.find((s) => s.id === selectedStation || s.id === parseInt(selectedStation));
    const stationName = stationObj ? stationObj.name : `Station ${selectedStation}`;
    const hourLabel = `${String(selectedHour).padStart(2, '0')}:00 – ${String(selectedHour).padStart(2, '0')}:59`;
    const title = `Playlist Log — ${stationName}`;
    const subtitle = `${selectedDate}  |  ${hourLabel}`;

    doc.setFontSize(16);
    doc.setTextColor(0, 180, 230);
    doc.text(title, 14, 18);
    doc.setFontSize(10);
    doc.setTextColor(120, 120, 120);
    doc.text(subtitle, 14, 26);
    doc.setTextColor(0, 0, 0);

    const columns = [
      { header: '#', dataKey: 'num' },
      { header: 'Time', dataKey: 'time' },
      { header: 'Title', dataKey: 'title' },
      { header: 'Artist', dataKey: 'artist' },
      { header: 'Category', dataKey: 'category' },
      { header: 'Duration', dataKey: 'duration' },
    ];

    const rows = [];
    let displayNum = 1;
    let pi = 0;

    while (pi < playlistItems.length) {
      const item = playlistItems[pi];

      if (item.type === 'mixblock_track') {
        const mbName = item.mixblock_name || 'Mixblock';
        const mbId = item.mixblock_id;
        const mbTracks = [];
        let mbDuration = 0;
        const mbTime = item.startTime
          ? new Date(item.startTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
          : '';
        while (pi < playlistItems.length && (
          (playlistItems[pi].type === 'mixblock_track' && playlistItems[pi].mixblock_id === mbId) ||
          ((playlistItems[pi].type === 'voicetrack' || playlistItems[pi].type === 'ai_voicetrack') && playlistItems[pi].mixblock_id === mbId)
        )) {
          mbTracks.push(playlistItems[pi]);
          mbDuration += playlistItems[pi].duration || 0;
          pi++;
        }
        const musicTracks = mbTracks.filter(t =>
          t.type === 'mixblock_track' && !t.is_sweeper && !t.isSweeper && t.play_as !== 'sweeper'
        );
        const mbMins = Math.floor(mbDuration / 60);
        const mbSecs = Math.floor(mbDuration % 60);
        const trackList = musicTracks.map((t, i) => `${i + 1}. ${t.artist || ''} - ${t.title || ''}`).join(' | ');
        rows.push({
          num: displayNum,
          time: mbTime,
          title: `${mbName} (${musicTracks.length} tracks): ${trackList}`,
          artist: '',
          category: 'Mixblock',
          duration: `${mbMins}:${String(mbSecs).padStart(2, '0')}`,
        });
        displayNum++;
        continue;
      }

      let categoryName = '';
      if (item.category && typeof item.category === 'object' && item.category.name) {
        categoryName = item.category.name;
      } else if (item.categories && item.categories.length > 0) {
        categoryName = item.categories[0].name || '';
      }
      if (item.type === 'voicetrack' || item.type === 'voice') categoryName = 'Voicetrack';
      if (item.type === 'ai_voicetrack') categoryName = 'AI Voicetrack';
      if (item.type === 'commercial_break') categoryName = 'Commercial';
      if (item.type === 'stream_relay') categoryName = 'Stream Relay';
      if (item.type === 'hard_fixed_timer') categoryName = 'Hard Timer';

      const timeStr = item.startTime
        ? new Date(item.startTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
        : '';
      const durationSecs = item.duration || 0;
      const mins = Math.floor(durationSecs / 60);
      const secs = Math.floor(durationSecs % 60);
      const durationStr = `${mins}:${String(secs).padStart(2, '0')}`;

      rows.push({
        num: displayNum,
        time: timeStr,
        title: item.title || '',
        artist: item.artist || '',
        category: categoryName,
        duration: durationStr,
      });
      displayNum++;
      pi++;
    }

    autoTable(doc, {
      startY: 32,
      columns,
      body: rows,
      theme: 'grid',
      headStyles: { fillColor: [0, 180, 230], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 8, cellPadding: 2 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: {
        num: { halign: 'center', cellWidth: 10 },
        time: { cellWidth: 28 },
        title: { cellWidth: 70 },
        artist: { cellWidth: 55 },
        category: { cellWidth: 55 },
        duration: { halign: 'center', cellWidth: 20 },
      },
    });

    doc.save(`playlist-log-${stationName.replace(/\s+/g, '-')}-${selectedDate}-${String(selectedHour).padStart(2, '0')}00.pdf`);
  };

  return (
    <Box>
      <PageHeader
        overline="Scheduling"
        title="Playlists"
        subtitle="Review and fine-tune each hour of your playout log"
        icon={<QueueMusic />}
      />

      {/* Date/Hour Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Grid container spacing={2} alignItems="center">
            {stations.length > 1 && (
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Station</InputLabel>
                  <Select
                    value={selectedStation || ''}
                    onChange={(e) => setSelectedStation(e.target.value)}
                    label="Station"
                  >
                    <MenuItem value="">Select Station</MenuItem>
                    {stations.map((station) => (
                      <MenuItem key={station.id} value={station.id}>
                        {station.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Date"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Hour</InputLabel>
                <Select
                  value={selectedHour}
                  onChange={(e) => setSelectedHour(e.target.value)}
                  label="Hour"
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <MenuItem key={i} value={i}>
                      {i.toString().padStart(2, '0')}:00
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {selectedStation && selectedDate && (
        <>
          {/* Playlist Items */}
          <Card sx={{ mb: 2, overflow: 'hidden' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1.5,
                px: 3,
                py: 2,
                borderBottom: '1px solid rgba(148,163,184,0.10)',
                background: 'linear-gradient(180deg, rgba(34,211,238,0.05) 0%, rgba(34,211,238,0) 100%)',
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ color: 'var(--rs-text)' }}>
                  Running Order
                </Typography>
                <Typography variant="caption" sx={{ color: 'var(--rs-muted)' }}>
                  {selectedItems.length === 1
                    ? '1 item selected — select another to insert a VT marker between them'
                    : selectedItems.length === 2
                      ? '2 items selected — the VT marker will drop between them'
                      : 'Drag rows to reorder · click rows to select for VT placement'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<Mic />}
                  onClick={addVTMarker}
                  disabled={!selectedStation || loading}
                  sx={{
                    borderColor: 'rgba(192,132,252,0.5)',
                    color: '#d8b4fe',
                    '&:hover': { borderColor: '#c084fc', bgcolor: 'rgba(192,132,252,0.08)' },
                  }}
                >
                  Add VT Marker
                  {selectedItems.length === 2 && ' (Between Selected)'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={openAddItemDialog}
                  sx={{
                    borderColor: 'rgba(34,211,238,0.5)',
                    color: 'var(--rs-accent)',
                    '&:hover': { borderColor: '#22d3ee', bgcolor: 'rgba(34,211,238,0.08)' },
                  }}
                >
                  Add Item
                </Button>
                {playlistItems.length > 0 && (
                  <Tooltip title="Download hour log as PDF">
                    <IconButton
                      onClick={downloadHourPdf}
                      sx={{
                        color: '#f87171',
                        border: '1px solid rgba(248,113,113,0.4)',
                        borderRadius: '10px',
                        '&:hover': { borderColor: '#f87171', bgcolor: 'rgba(248,113,113,0.08)' },
                      }}
                    >
                      <PictureAsPdf fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSavePlaylist}
                  disabled={saving || !selectedStation || !playlistMatchesSelection}
                  sx={{
                    backgroundImage: 'linear-gradient(180deg, #34d399 0%, #10b981 100%)',
                    color: '#052e1c',
                    '&:hover': {
                      backgroundImage: 'linear-gradient(180deg, #4ade80 0%, #22c55e 100%)',
                      boxShadow: '0 6px 22px -6px rgba(34,197,94,0.55)',
                    },
                  }}
                >
                  {saving ? 'Saving...' : 'Save Playlist'}
                </Button>
              </Box>
            </Box>
            <CardContent sx={{ pt: 2 }}>
              {loading ? (
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  Loading playlist...
                </Typography>
              ) : playlistItems.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 7 }}>
                  <QueueMusic sx={{ fontSize: 52, color: 'rgba(148,163,184,0.25)', mb: 1 }} />
                  <Typography variant="subtitle1" sx={{ color: 'var(--rs-text)', fontWeight: 600 }}>
                    Nothing scheduled this hour
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'var(--rs-muted)', mb: 2.5 }}>
                    Start building the running order for {String(selectedHour).padStart(2, '0')}:00.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={openAddItemDialog}
                  >
                    Add First Item
                  </Button>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small" sx={tableSx}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headCellSx}>#</TableCell>
                        <TableCell sx={headCellSx}>Time</TableCell>
                        <TableCell sx={{ ...headCellSx, width: 40, p: 0.5 }}></TableCell>
                        <TableCell sx={headCellSx}>Title</TableCell>
                        <TableCell sx={headCellSx}>Artist</TableCell>
                        <TableCell sx={headCellSx}>Duration</TableCell>
                        <TableCell sx={headCellSx}>Play</TableCell>
                        <TableCell sx={{ ...headCellSx, textAlign: 'right' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groupedPlaylistEntries.map((entry) => {

                        // ---- Collapsible Commercial Break Group ----
                        if (entry.type === 'commercial_break_group') {
                          const isExpanded = expandedBreaks.has(entry.groupStartIndex);
                          const rows = [];
                          rows.push(
                            <TableRow
                              key={`break-group-${entry.groupStartIndex}`}
                              onClick={() => toggleBreakExpanded(entry.groupStartIndex)}
                              sx={{
                                bgcolor: hexToRgba('#f59e0b', 0.09),
                                '&:hover': { bgcolor: hexToRgba('#f59e0b', 0.16) },
                                cursor: 'pointer',
                                '& td:first-of-type': accentCellSx('#f59e0b'),
                              }}
                            >
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {isExpanded ? <ExpandLess sx={{ color: '#f59e0b' }} /> : <ExpandMore sx={{ color: '#f59e0b' }} />}
                                  {entry.displayNum}
                                </Box>
                              </TableCell>
                              <TableCell sx={timeCellSx}>{formatTime(entry.startTime)}</TableCell>
                              <TableCell sx={{ width: 40, p: 0.5 }} />
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Chip label="COMMERCIAL" size="small" sx={{ bgcolor: hexToRgba('#f59e0b', 0.18), color: '#fbbf24', border: '1px solid rgba(245,158,11,0.45)', fontWeight: 700, fontSize: '0.65rem' }} />
                                  <span style={{ fontWeight: 600 }}>Commercial Break</span>
                                  <Typography component="span" variant="body2" sx={{ color: 'var(--rs-muted)' }}>
                                    {entry.spotCount} spot{entry.spotCount !== 1 ? 's' : ''}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell />
                              <TableCell sx={timeCellSx}>{formatDuration(entry.totalDuration || 0)}</TableCell>
                              <TableCell />
                              <TableCell />
                            </TableRow>
                          );
                          if (isExpanded) {
                            let adTime = entry.startTime ? new Date(entry.startTime) : null;
                            entry.items.forEach((adItem, adIdx) => {
                              const adOrigIdx = adItem.originalIndex;
                              const adStartFormatted = adTime ? formatTime(adTime.toISOString()) : formatTime(adItem.startTime);
                              rows.push(
                                <TableRow
                                  key={`break-ad-${adOrigIdx}`}
                                  sx={{
                                    bgcolor: hexToRgba('#f59e0b', 0.04),
                                    '&:hover': { bgcolor: hexToRgba('#f59e0b', 0.10) },
                                    '& td:first-of-type': accentCellSx(hexToRgba('#f59e0b', 0.4)),
                                  }}
                                >
                                  <TableCell sx={{ pl: 5, color: 'var(--rs-muted)' }}>{adIdx + 1}</TableCell>
                                  <TableCell sx={{ ...timeCellSx, fontSize: '0.75rem' }}>{adStartFormatted}</TableCell>
                                  <TableCell sx={{ width: 40, p: 0.5 }} />
                                  <TableCell sx={{ fontSize: '0.85rem' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Chip label="AD" size="small" sx={{ bgcolor: hexToRgba('#f59e0b', 0.14), color: '#fbbf24', border: '1px solid rgba(245,158,11,0.35)', fontSize: '0.65rem', fontWeight: 700, height: 20 }} />
                                      {adItem.title || 'Spot'}
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>{adItem.artist || ''}</TableCell>
                                  <TableCell sx={{ ...timeCellSx, fontSize: '0.75rem' }}>{formatDuration(adItem.duration || 0)}</TableCell>
                                  <TableCell>
                                    {adItem.filepath && (
                                      <>
                                        {playingIndex === adOrigIdx ? (
                                          <>
                                            <IconButton size="small" onClick={handlePauseItem} sx={{ color: '#22d3ee' }}><Pause fontSize="small" /></IconButton>
                                            <IconButton size="small" onClick={handleStopItem} sx={{ color: '#ff6b6b' }}><Stop fontSize="small" /></IconButton>
                                          </>
                                        ) : (
                                          <IconButton size="small" onClick={() => handlePlayItem(adOrigIdx)} sx={{ color: '#4ade80' }}><PlayArrow fontSize="small" /></IconButton>
                                        )}
                                      </>
                                    )}
                                  </TableCell>
                                  <TableCell />
                                </TableRow>
                              );
                              if (adTime) {
                                adTime = new Date(adTime.getTime() + (adItem.duration || 0) * 1000);
                              }
                            });
                          }
                          return rows;
                        }

                        // ---- Collapsible Mixblock Group ----
                        if (entry.type === 'mixblock_group') {
                          const isExpanded = expandedMixblocks.has(entry.groupStartIndex);
                          const hasMix = entry.mix_status === 'completed' && entry.mix_asset_id;
                          const mixPending = entry.mix_status === 'pending' || entry.mix_status === 'processing';
                          const isMixPlaying = playingMixAssetId === entry.mix_asset_id;
                          const rows = [];
                          rows.push(
                            <TableRow
                              key={`mb-group-${entry.groupStartIndex}`}
                              onClick={() => toggleMixblockExpanded(entry.groupStartIndex)}
                              sx={{
                                bgcolor: hexToRgba('#c084fc', 0.09),
                                '&:hover': { bgcolor: hexToRgba('#c084fc', 0.16) },
                                cursor: 'pointer',
                                '& td:first-of-type': accentCellSx('#c084fc'),
                              }}
                            >
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {isExpanded ? <ExpandLess sx={{ color: '#c084fc' }} /> : <ExpandMore sx={{ color: '#c084fc' }} />}
                                  {entry.displayNum}
                                </Box>
                              </TableCell>
                              <TableCell sx={timeCellSx}>{formatTime(entry.startTime)}</TableCell>
                              <TableCell sx={{ width: 40, p: 0.5 }} />
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Chip label="MIXBLOCK" size="small" sx={{ bgcolor: hexToRgba('#c084fc', 0.18), color: '#d8b4fe', border: '1px solid rgba(192,132,252,0.45)', fontWeight: 700, fontSize: '0.65rem' }} />
                                  <span style={{ fontWeight: 600 }}>{entry.mixblock_name}</span>
                                  <Typography component="span" variant="body2" sx={{ color: 'var(--rs-muted)' }}>
                                    {entry.trackCount} track{entry.trackCount !== 1 ? 's' : ''}
                                  </Typography>
                                  {hasMix && (
                                    <Chip label="MIXED" size="small" icon={<CheckCircle sx={{ fontSize: 14 }} />}
                                      sx={{ bgcolor: hexToRgba('#22c55e', 0.15), color: '#4ade80', border: '1px solid rgba(34,197,94,0.4)', fontSize: '0.65rem', fontWeight: 700, height: 20, '& .MuiChip-icon': { color: '#4ade80' } }} />
                                  )}
                                  {mixPending && (
                                    <Chip label={entry.mix_status === 'processing' ? 'MIXING...' : 'QUEUED'} size="small"
                                      icon={<HourglassEmpty sx={{ fontSize: 14 }} />}
                                      sx={{ bgcolor: hexToRgba('#f59e0b', 0.15), color: '#fbbf24', border: '1px solid rgba(245,158,11,0.4)', fontSize: '0.65rem', fontWeight: 700, height: 20, '& .MuiChip-icon': { color: '#fbbf24' } }} />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell />
                              <TableCell sx={timeCellSx}>{formatDuration(entry.totalDuration || 0)}</TableCell>
                              <TableCell>
                                {hasMix && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                                    {isMixPlaying ? (
                                      <>
                                        <IconButton size="small" onClick={() => handleSkipMixTrack(entry.mix_asset_id, entry.mix_track_offsets, 'prev')}
                                          sx={{ color: '#c084fc' }}><SkipPrevious fontSize="small" /></IconButton>
                                        <IconButton size="small" onClick={handlePauseItem}
                                          sx={{ color: '#22d3ee' }}><Pause fontSize="small" /></IconButton>
                                        <IconButton size="small" onClick={handleStopItem}
                                          sx={{ color: '#ff6b6b' }}><Stop fontSize="small" /></IconButton>
                                        <IconButton size="small" onClick={() => handleSkipMixTrack(entry.mix_asset_id, entry.mix_track_offsets, 'next')}
                                          sx={{ color: '#c084fc' }}><SkipNext fontSize="small" /></IconButton>
                                        {mixPlayingTrackIdx != null && entry.mix_track_offsets?.[mixPlayingTrackIdx] && (
                                          <Chip label={`${mixPlayingTrackIdx + 1}/${entry.mix_track_offsets.length}`}
                                            size="small" sx={{ bgcolor: hexToRgba('#c084fc', 0.2), color: '#d8b4fe', border: '1px solid rgba(192,132,252,0.45)', fontSize: '0.65rem', height: 18, ml: 0.5 }} />
                                        )}
                                      </>
                                    ) : (
                                      <Tooltip title="Play beatmixed version">
                                        <IconButton size="small" onClick={() => handlePlayMixedFile(entry.mix_asset_id, entry.mix_track_offsets)}
                                          sx={{ color: '#4ade80' }}><PlayArrow fontSize="small" /></IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell />
                            </TableRow>
                          );
                          if (isExpanded) {
                            let trackTime = entry.startTime ? new Date(entry.startTime) : null;
                            let trackNum = 0;
                            entry.items.forEach((mbItem) => {
                              const mbOrigIdx = mbItem.originalIndex;
                              const mbStartFormatted = trackTime ? formatTime(trackTime.toISOString()) : formatTime(mbItem.startTime);
                              const isSweeper = mbItem.is_sweeper || mbItem.isSweeper || mbItem.play_as === 'sweeper';
                              const isVt = mbItem.type === 'voicetrack';
                              const isAiVt = mbItem.type === 'ai_voicetrack';
                              const isOverlay = isSweeper || isVt || isAiVt;
                              if (!isOverlay) trackNum++;

                              const chipLabel = isSweeper ? 'SWP'
                                : isVt ? 'VT'
                                : isAiVt ? 'AI VT'
                                : `${trackNum}/${entry.trackCount}`;
                              const chipColor = isSweeper ? '#f59e0b'
                                : isVt ? '#4ade80'
                                : isAiVt ? '#c084fc'
                                : '#c084fc';

                              rows.push(
                                <TableRow
                                  key={`mb-track-${mbOrigIdx}`}
                                  sx={{
                                    bgcolor: hexToRgba(chipColor, isOverlay ? 0.03 : 0.05),
                                    '&:hover': { bgcolor: hexToRgba(chipColor, isOverlay ? 0.08 : 0.11) },
                                    '& td:first-of-type': accentCellSx(hexToRgba(chipColor, 0.4)),
                                  }}
                                >
                                  <TableCell sx={{ pl: 5, color: 'var(--rs-muted)' }}>{isOverlay ? '' : trackNum}</TableCell>
                                  <TableCell sx={{ ...timeCellSx, fontSize: '0.75rem' }}>{mbStartFormatted}</TableCell>
                                  <TableCell sx={{ width: 40, p: 0.5 }} />
                                  <TableCell sx={{ fontSize: '0.85rem' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Chip label={chipLabel} size="small"
                                        sx={{ bgcolor: hexToRgba(chipColor, 0.15), color: chipColor, border: `1px solid ${hexToRgba(chipColor, 0.4)}`, fontSize: '0.65rem', fontWeight: 700, height: 20 }} />
                                      {mbItem.title || 'Track'}
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>{mbItem.artist || ''}</TableCell>
                                  <TableCell sx={{ fontSize: '0.85rem' }} />
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      {hasMix && !isOverlay && (
                                        <Tooltip title={`Skip to track ${trackNum} in mix`}>
                                          <IconButton size="small"
                                            onClick={() => handlePlayMixedFile(entry.mix_asset_id, entry.mix_track_offsets, trackNum - 1)}
                                            sx={{
                                              color: isMixPlaying && mixPlayingTrackIdx === trackNum - 1 ? '#c084fc' : '#4ade80',
                                              border: isMixPlaying && mixPlayingTrackIdx === trackNum - 1 ? '1px solid #c084fc' : 'none',
                                            }}>
                                            <PlayArrow fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                      {!hasMix && mbItem.filepath && (
                                        <>
                                          {playingIndex === mbOrigIdx ? (
                                            <>
                                              <IconButton size="small" onClick={handlePauseItem} sx={{ color: '#22d3ee' }}><Pause fontSize="small" /></IconButton>
                                              <IconButton size="small" onClick={handleStopItem} sx={{ color: '#ff6b6b' }}><Stop fontSize="small" /></IconButton>
                                            </>
                                          ) : (
                                            <IconButton size="small" onClick={() => handlePlayItem(mbOrigIdx)} sx={{ color: '#4ade80' }}><PlayArrow fontSize="small" /></IconButton>
                                          )}
                                        </>
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell />
                                </TableRow>
                              );
                              if (trackTime) {
                                trackTime = new Date(trackTime.getTime() + (mbItem.duration || 0) * 1000);
                              }
                            });
                          }
                          return rows;
                        }

                        // ---- Normal item (non-commercial, non-mixblock) ----
                        const item = entry;
                        const index = entry.originalIndex;
                        let categoryName = 'Unknown';
                        let categoryColor = '#666666';
                        
                        if (item.category) {
                          if (typeof item.category === 'object' && item.category !== null && item.category.name) {
                            categoryName = item.category.name;
                            categoryColor = item.category.color || '#666666';
                          }
                        }
                        if (categoryName === 'Unknown' && item.categories && Array.isArray(item.categories) && item.categories.length > 0) {
                          const firstCategory = item.categories[0];
                          if (firstCategory && typeof firstCategory === 'object' && firstCategory.name) {
                            categoryName = firstCategory.name;
                            categoryColor = firstCategory.color || '#666666';
                          }
                        } else if (item.asset_id) {
                          const assetId = typeof item.asset_id === 'string' ? parseInt(item.asset_id, 10) : item.asset_id;
                          if (!isNaN(assetId) && assetById.size > 0) {
                            const asset = assetById.get(assetId);
                            if (asset && asset.categories && Array.isArray(asset.categories) && asset.categories.length > 0) {
                              categoryName = asset.categories[0].name || 'Unknown';
                              categoryColor = asset.categories[0].color || '#666666';
                            }
                          }
                        }
                        // Special types (these won't be commercial_break since those are grouped)
                        if (item.type === 'voicetrack' || item.type === 'voice') {
                          categoryName = 'Voicetrack';
                          categoryColor = '#c084fc';
                        } else if (item.type === 'ai_voicetrack') {
                          categoryName = 'AI Voicetrack';
                          categoryColor = '#c084fc';
                        } else if (item.type === 'stream_relay') {
                          categoryName = 'Stream Relay';
                          categoryColor = '#607d8b';
                        } else if (item.type === 'hard_fixed_timer') {
                          categoryName = 'Hard Timer';
                          categoryColor = '#ef4444';
                        } else if (item.type === 'mixblock_track') {
                          if (categoryName === 'Unknown') {
                            categoryName = item.mixblock_name || 'Mixblock';
                            categoryColor = '#c084fc';
                          }
                        }
                        
                        const isVoicetrackMarker = item.type === 'voicetrack' || item.type === 'voice';
                        const isHardFixedTimer = item.type === 'hard_fixed_timer';
                        const canReplaceItem = !isVoicetrackMarker && !isHardFixedTimer
                          && item.type !== 'stream_relay'
                          && item.type !== 'soft_time_marker'
                          && item.type !== 'ai_voicetrack'
                          && item.type !== 'mixblock_track'
                          && item.type !== 'commercial_break';
                        const savedVts = (isVoicetrackMarker && item.id && voicetracksByPlayoutLog[item.id]) || [];
                        const hasAudio = (item.asset_id || item.filepath) || (isVoicetrackMarker && (item.filepath || item.voicetrack_id || savedVts.length > 0));
                        const isSelected = selectedItemSet.has(index);
                        
                        return (
                          <TableRow 
                            key={index}
                            draggable
                            onDragStart={() => handleDragStart(index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragEnd={() => handleDragEnd()}
                            onClick={(e) => {
                              if (isVoicetrackMarker) return;
                              if (e.target.closest('button') || e.target.closest('[role="button"]') || e.target.closest('svg')) {
                                return;
                              }
                              handleItemSelect(index);
                            }}
                            sx={{
                              bgcolor: isSelected
                                ? hexToRgba('#22d3ee', 0.14)
                                : hexToRgba(categoryColor, 0.07),
                              '&:hover': {
                                bgcolor: isSelected
                                  ? hexToRgba('#22d3ee', 0.20)
                                  : hexToRgba(categoryColor, 0.14),
                              },
                              cursor: draggedIndex === index ? 'grabbing' : 'grab',
                              opacity: draggedIndex === index ? 0.5 : 1,
                              '& td:first-of-type': accentCellSx(isSelected ? '#22d3ee' : categoryColor),
                              outline: isSelected ? '1px solid rgba(34,211,238,0.55)' : 'none',
                              outlineOffset: '-1px',
                              transition: 'background-color 120ms ease',
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DragIndicator
                                  fontSize="small"
                                  sx={{
                                    color: 'rgba(148,163,184,0.35)',
                                    cursor: 'grab',
                                    '&:active': { cursor: 'grabbing' },
                                    'tr:hover &': { color: 'rgba(148,163,184,0.7)' },
                                  }}
                                />
                                <Typography component="span" variant="body2" className="tabular-nums" sx={{ color: 'var(--rs-muted)', minWidth: 18 }}>
                                  {entry.displayNum}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={timeCellSx}>{formatTime(entry.startTime)}</TableCell>
                            <TableCell sx={{ width: 40, p: 0.5 }}>
                              {!isVoicetrackMarker
                                && hasSystemFeature('ai_insights')
                                && item.category?.ai_insights_enabled && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); setAiInsightsItem(item); setAiInsightsOpen(true); }}
                                  title="AI Insights"
                                  sx={{ color: '#22d3ee' }}
                                >
                                  <Psychology fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                            <TableCell>
                              {isVoicetrackMarker ? (
                                <Chip
                                  icon={<Mic sx={{ fontSize: 14 }} />}
                                  label="VOICE"
                                  size="small"
                                  sx={{
                                    bgcolor: hexToRgba('#c084fc', 0.15),
                                    color: '#d8b4fe',
                                    border: '1px solid rgba(192,132,252,0.45)',
                                    fontWeight: 700,
                                    fontSize: '0.65rem',
                                    '& .MuiChip-icon': { color: '#d8b4fe' },
                                  }}
                                />
                              ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography component="span" variant="body2" sx={{ color: 'var(--rs-text)', fontWeight: 600 }}>
                                    {item.title}
                                  </Typography>
                                  {item.type === 'mixblock_track' && item.mixblock_position != null && (
                                    <Chip
                                      label={`Mix ${item.mixblock_position + 1}/${item.mixblock_total || '?'}`}
                                      size="small"
                                      sx={{ bgcolor: hexToRgba('#c084fc', 0.15), color: '#d8b4fe', border: '1px solid rgba(192,132,252,0.4)', fontSize: '0.65rem', height: 18, ml: 0.5 }}
                                    />
                                  )}
                                </Box>
                              )}
                            </TableCell>
                            <TableCell sx={{ color: 'var(--rs-muted)', fontSize: '0.85rem' }}>{item.artist}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography component="span" sx={timeCellSx}>{formatDuration(item.duration || 0)}</Typography>
                                {item.intro && item.intro > 0 && (
                                  <Chip
                                    label={`Intro ${item.intro.toFixed(1)}s`}
                                    size="small"
                                    sx={{
                                      bgcolor: hexToRgba('#22c55e', 0.14),
                                      color: '#4ade80',
                                      border: '1px solid rgba(34,197,94,0.4)',
                                      fontSize: '0.65rem',
                                      fontWeight: 700,
                                      height: 20,
                                    }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                {/* Saved voicetracks (including split per region) - each with play button */}
                                {isVoicetrackMarker && savedVts.length > 0 && savedVts.map((vt) => {
                                  const vtRegionName = vt.region_id && regions.length
                                    ? (regionNameById.get(vt.region_id) || `R${vt.region_id}`)
                                    : 'Shared';
                                  const isVtPlaying = playingVoicetrackId === vt.id;
                                  return (
                                    <Box key={vt.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Chip
                                        label={vtRegionName}
                                        size="small"
                                        sx={{ fontSize: '0.65rem', height: 20, bgcolor: 'rgba(148,163,184,0.10)', color: 'var(--rs-muted)', border: '1px solid rgba(148,163,184,0.25)' }}
                                      />
                                      {isVtPlaying ? (
                                        <IconButton
                                          size="small"
                                          onClick={(e) => { e.stopPropagation(); handlePauseItem(); }}
                                          sx={{ color: '#22d3ee', p: 0.25 }}
                                        >
                                          <Pause fontSize="small" />
                                        </IconButton>
                                      ) : (
                                        <IconButton
                                          size="small"
                                          onClick={(e) => { e.stopPropagation(); handlePlayVoicetrackById(vt.id); }}
                                          sx={{ color: '#4ade80', p: 0.25 }}
                                        >
                                          <PlayArrow fontSize="small" />
                                        </IconButton>
                                      )}
                                    </Box>
                                  );
                                })}
                                {/* Fallback: single play button when no savedVts but item has audio */}
                                {hasAudio && savedVts.length === 0 && (
                                  <>
                                    {playingIndex === index ? (
                                      <>
                                        <IconButton size="small" onClick={handlePauseItem} sx={{ color: '#22d3ee' }}>
                                          <Pause fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={handleStopItem} sx={{ color: '#ff6b6b' }}>
                                          <Stop fontSize="small" />
                                        </IconButton>
                                      </>
                                    ) : (
                                      <IconButton
                                        size="small"
                                        onClick={() => handlePlayItem(index)}
                                        sx={{ color: '#4ade80' }}
                                      >
                                        <PlayArrow fontSize="small" />
                                      </IconButton>
                                    )}
                                  </>
                                )}
                              </Box>
                            </TableCell>
                          <TableCell sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {isVoicetrackMarker ? (
                              <>
                                <Chip
                                  label="VT Marker"
                                  size="small"
                                  sx={{
                                    bgcolor: hexToRgba('#c084fc', 0.15),
                                    color: '#d8b4fe',
                                    border: '1px solid rgba(192,132,252,0.4)',
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                    mr: 0.5,
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveItem(index)}
                                  sx={{ color: 'rgba(248,113,113,0.7)', '&:hover': { color: '#f87171', bgcolor: 'rgba(248,113,113,0.1)' } }}
                                  title="Remove VT marker from playlist"
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </>
                            ) : (
                              <>
                                {canReplaceItem && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleReplaceItem(index)}
                                    sx={{ color: 'var(--rs-accent)', '&:hover': { color: 'var(--rs-accent)', bgcolor: 'rgba(34,211,238,0.1)' } }}
                                    title="Replace with a similar asset"
                                  >
                                    <SwapHoriz fontSize="small" />
                                  </IconButton>
                                )}
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveItem(index)}
                                  sx={{ color: 'rgba(248,113,113,0.7)', '&:hover': { color: '#f87171', bgcolor: 'rgba(248,113,113,0.1)' } }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Add / Replace Item Dialog */}
      <Dialog open={addItemDialogOpen} onClose={closeItemDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {replacingItem
            ? `Replace “${replacingItem.title || 'item'}”`
            : 'Add Item to Playlist'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {!selectedStation ? (
              <Grid item xs={12}>
                <Alert severity="warning">
                  Please select a station first to load assets.
                </Alert>
              </Grid>
            ) : null}

            {replacingItem && (
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: 'var(--rs-muted)', mb: 1 }}>
                  Suggestions from the same category, type, and similar duration. Click one to insert it in this slot.
                </Typography>
                {replacementSuggestions.length === 0 ? (
                  <Alert severity="info">
                    No close matches yet. Use search below to pick another asset from this category.
                  </Alert>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {replacementSuggestions.map((asset) => {
                      const selected = Number(newItem.asset_id) === Number(asset.id);
                      return (
                        <Box
                          key={asset.id}
                          onClick={() => {
                            setNewItem({
                              ...newItem,
                              asset_id: asset.id.toString(),
                              duration: asset.duration ?? newItem.duration ?? 180,
                            });
                          }}
                          onDoubleClick={() => applySelectedAsset(asset)}
                          sx={{
                            cursor: 'pointer',
                            px: 1.5,
                            py: 1.25,
                            borderRadius: 1.5,
                            border: selected
                              ? '1px solid rgba(34,211,238,0.7)'
                              : '1px solid rgba(148,163,184,0.18)',
                            bgcolor: selected ? 'rgba(34,211,238,0.12)' : 'var(--rs-surface)',
                            '&:hover': {
                              borderColor: 'rgba(34,211,238,0.55)',
                              bgcolor: 'rgba(34,211,238,0.08)',
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {asset.title || 'Unknown'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {asset.artist || 'Unknown Artist'} · {formatDuration(asset.duration)}
                              </Typography>
                            </Box>
                            <Button
                              size="small"
                              variant={selected ? 'contained' : 'outlined'}
                              onClick={(e) => {
                                e.stopPropagation();
                                applySelectedAsset(asset);
                              }}
                              sx={{
                                minWidth: 88,
                                bgcolor: selected ? '#22d3ee' : 'transparent',
                                color: selected ? '#042f2e' : 'var(--rs-accent)',
                                borderColor: 'rgba(34,211,238,0.5)',
                                '&:hover': { bgcolor: selected ? '#67e8f9' : 'rgba(34,211,238,0.12)' },
                              }}
                            >
                              Use
                            </Button>
                          </Box>
                          {asset.categories?.length > 0 && (
                            <Box sx={{ mt: 0.75, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {asset.categories.map((cat) => (
                                <Chip
                                  key={cat.id}
                                  label={cat.name}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.7rem',
                                    bgcolor: cat.color || '#666',
                                    color: 'var(--rs-text)',
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Grid>
            )}
            
            {/* Category Filter */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>{replacingItem ? 'Category' : 'Filter by Category (Optional)'}</InputLabel>
                <Select
                  value={selectedCategoryFilter}
                  onChange={(e) => {
                    const next = e.target.value == null ? '' : String(e.target.value);
                    setSelectedCategoryFilter(next);
                    autoSuggestedRef.current = false;
                    const selectedAsset = assets.find((a) => Number(a.id) === Number(newItem.asset_id));
                    const stillValid = !next || selectedAsset?.categories?.some(
                      (cat) => Number(cat.id) === Number(next)
                    );
                    if (!stillValid) {
                      setNewItem((prev) => ({ ...prev, asset_id: '' }));
                    }
                  }}
                  label={replacingItem ? 'Category' : 'Filter by Category (Optional)'}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={String(category.id)}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Asset Search/Select */}
            <Grid item xs={12}>
              <Autocomplete
                options={assets.filter(asset => {
                  if (replacingItem && Number(asset.id) === Number(replacingItem.asset_id)) {
                    return false;
                  }
                  // Filter by category if selected
                  if (selectedCategoryFilter) {
                    const hasCategory = asset.categories && asset.categories.some(
                      cat => Number(cat.id) === Number(selectedCategoryFilter)
                    );
                    if (!hasCategory) return false;
                  }
                  return true;
                })}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return `${option.title || 'Unknown'} - ${option.artist || 'Unknown'}`;
                }}
                value={assets.find(a => Number(a.id) === Number(newItem.asset_id)) || null}
                onChange={(e, value) => {
                  setNewItem({
                    ...newItem,
                    asset_id: value ? value.id.toString() : '',
                    duration: value?.duration ?? newItem.duration ?? 180
                  });
                }}
                filterOptions={(options, params) => {
                  const searchTerm = params.inputValue.toLowerCase();
                  const filtered = options.filter((option) => {
                    const title = (option.title || '').toLowerCase();
                    const artist = (option.artist || '').toLowerCase();
                    const matchesSearch = !searchTerm || title.includes(searchTerm) || artist.includes(searchTerm);
                    return matchesSearch;
                  });
                  return filtered;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={replacingItem ? 'Or search for a different asset' : 'Search and Select Asset'}
                    placeholder="Type to search by title or artist..."
                    helperText={selectedCategoryFilter ? `Showing assets from selected category` : "Search assets by title or artist"}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="body1">
                        {option.title || 'Unknown'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.artist || 'Unknown Artist'}
                      </Typography>
                      {option.categories && option.categories.length > 0 && (
                        <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {option.categories.map((cat) => (
                            <Chip
                              key={cat.id}
                              label={cat.name}
                              size="small"
                              sx={{ 
                                height: 20, 
                                fontSize: '0.7rem',
                                bgcolor: cat.color || '#666',
                                color: 'var(--rs-text)'
                              }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
                noOptionsText={
                  !selectedStation 
                    ? "Please select a station first" 
                    : assets.length === 0 
                      ? "No assets available. Make sure a station is selected." 
                      : selectedCategoryFilter
                        ? "No assets found in this category"
                        : "No assets found matching your search"
                }
                loading={loading}
                disabled={!selectedStation}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeItemDialog}>Cancel</Button>
          <Button 
            onClick={handleAddItem} 
            variant="contained" 
            disabled={!newItem.asset_id || !selectedStation}
            sx={{ bgcolor: '#22d3ee' }}
          >
            {replacingItem ? 'Replace' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <AiInsightsDialog
        open={aiInsightsOpen}
        onClose={() => { setAiInsightsOpen(false); setAiInsightsItem(null); }}
        item={aiInsightsItem}
        stationId={selectedStation || null}
      />
    </Box>
  );
}
